import { useCallback, type ChangeEvent, type FC } from 'react';
import { graphql, useFragment } from 'react-relay';
import { useDispatch } from 'react-redux';
import { updateUriRef } from '../../../actions/filterActions.ts';
import { useAppliedFilterUrlLabels } from '../../helpers/appliedFilters/useAppliedFilterUrlLabel.ts';

import { SbRespRefineMenuStoneCutSelectInput } from './SbRespRefineMenuStoneCutSelectInput.tsx';

import { type SbRespRefineMenuStoneCutSelect_itemSearch$key } from './__generated__/SbRespRefineMenuStoneCutSelect_itemSearch.graphql.ts';

const itemSearchFragment = graphql`
    fragment SbRespRefineMenuStoneCutSelect_itemSearch on ItemSearchQueryConnection {
        searchResultData {
            vectorResultCount
        }
        ...useAppliedFilterUrlLabel_itemSearch
    }
`;

type FilterValue = {
    readonly count?: number | null;
    readonly displayName?: string | null;
    readonly linkable?: boolean | null;
    readonly linkReference?: string | null;
    readonly urlLabel?: string | null;
    isHidden?: boolean;
};

type Props = {
    itemSearch: SbRespRefineMenuStoneCutSelect_itemSearch$key;
    valuesToShow: Array<FilterValue>;
    filterName: string;
    stoneCutMap: Record<string, FC<{ className?: string }>>;
    trackingOptions?: Record<string, unknown>;
};

export const SbRespRefineMenuStoneCutSelect: FC<Props> = ({
    itemSearch: itemSearchRef,
    valuesToShow,
    filterName,
    stoneCutMap,
    trackingOptions,
}) => {
    const dispatch = useDispatch();
    const itemSearch = useFragment(itemSearchFragment, itemSearchRef);
    const appliedFilterUrlLabels = useAppliedFilterUrlLabels(itemSearch, filterName);
    const hasVectorSearchResults = !!itemSearch?.searchResultData?.vectorResultCount;
    const handleFilterChange = useCallback(
        (filterValue: FilterValue, isDeselected: boolean, event: ChangeEvent<HTMLInputElement>) =>
            dispatch(
                updateUriRef({ filterName, filterValue, isDeselected, event, trackingOptions })
            ),
        [dispatch, filterName, trackingOptions]
    );

    return (
        <div>
            {valuesToShow.map((item, index) => {
                const { urlLabel, displayName, count, linkReference, linkable, isHidden } = item;
                if (!urlLabel || !displayName) {
                    return null;
                }
                const isChecked = appliedFilterUrlLabels.some(
                    appliedUrlLabel => appliedUrlLabel === urlLabel
                );
                return (
                    <SbRespRefineMenuStoneCutSelectInput
                        count={count}
                        checked={isChecked}
                        linkReference={linkReference}
                        linkable={linkable}
                        onChange={event => handleFilterChange(item, isChecked, event)}
                        text={displayName}
                        stoneCut={urlLabel}
                        key={`${item.urlLabel}-${index}`}
                        stoneCutMap={stoneCutMap}
                        isHidden={isHidden}
                        hasVectorSearchResults={hasVectorSearchResults}
                    />
                );
            })}
        </div>
    );
};
